import * as React from 'react';
import { BodyPrimary, H6 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SavedSearch } from '@/redux/api/saveSearch';
import isEqual from 'react-fast-compare';
import ManageSavedSearchCard, { ManageSavedSearchCardProps } from './ManageSavedSearchCard';
import MobileSlideMenu from './MobileSlideMenu';
import styled, { useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    className?: string;
    preparedSavedSearchFilter: React.ReactElement;
    searches?: SavedSearch[];
};

const ManageSavedSearchGrid = (props: Props) => {
    const { className, preparedSavedSearchFilter, searches } = props;
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);

    const mapSavedSearchCards = (search: SavedSearch, index: number) => {
        const cardProps: ManageSavedSearchCardProps = {
            search,
        };

        return (
            <ManageSavedSearchCard
                key={index}
                {...cardProps}
            />
        );
    };

    return (
        <Grid className={className}>
            {isMobile ? (
                <MobileHeader>
                    {searches.length === 1 ? (
                        <BodyPrimary $darkGrey>
                            <FormattedMessage id="search_alerts.singleSearchTerm" />
                        </BodyPrimary>
                    ) : (
                        <BodyPrimary $darkGrey>
                            <FormattedMessage
                                id="search_alerts.countSearchTerms"
                                values={{ count: searches.length }}
                            />
                        </BodyPrimary>
                    )}
                    <MobileSlideMenu preparedSavedSearchFilter={preparedSavedSearchFilter} />
                </MobileHeader>
            ) : (
                <HeaderRow>
                    <SearchColumn>
                        <FormattedMessage id="name" />
                    </SearchColumn>
                    <NewColumn>
                        <FormattedMessage id="search_alerts.newInPast24Hrs" />
                    </NewColumn>
                    <DateColumn>
                        <FormattedMessage id="search_alerts.dateAdded" />
                    </DateColumn>
                </HeaderRow>
            )}
            {searches.map(mapSavedSearchCards)}
        </Grid>
    );
};

export default React.memo<Props>(ManageSavedSearchGrid, isEqual);

const Grid = styled.div`
    padding: 0;
    width: 100%;
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: auto 150px 140px 115px;
    grid-template-rows: auto;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    padding-bottom: 6px;
`;

const SearchColumn = styled(H6)`
    grid-column: 1 / 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0;
`;

const NewColumn = styled(H6)`
    padding-left: 16px;
    grid-column: 2 / 3;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
`;

const DateColumn = styled(H6)`
    padding-left: 16px;
    grid-column: 3 / 4;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const MobileHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
`;
